<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    import { cursorTarget, canvasInstance, viewport } from '../../stores';
    import { get } from 'svelte/store';

    let currentX = 0;
    let currentY = 0;
    let targetX = 0;
    let targetY = 0;
    let animationFrame: number;
    let fadeTimeout: number;
	let isVisible = false;

    const lerpSpeed = 0.08;
    const fadeDelay = 4000;

    function lerp(start: number, end: number, t: number): number {
        return start + (end - start) * t;
    }

    function updatePosition() {
        currentX = lerp(currentX, targetX, lerpSpeed);
        currentY = lerp(currentY, targetY, lerpSpeed);

        animationFrame = requestAnimationFrame(updatePosition);
    }

    function resetFadeTimeout() {
        if (fadeTimeout) {
            clearTimeout(fadeTimeout);
        }
        
        isVisible = true;
        
        fadeTimeout = window.setTimeout(() => {
            isVisible = false;
        }, fadeDelay);
    }

    const unsubscribe = cursorTarget.subscribe((target) => {
        // Only move cursor when source is 'remote_agent'
        if (target && target.source === 'remote_agent') {
            const canvas = get(canvasInstance);
            
            if (canvas) {
                // Use DirectCanvas's worldToScreen to properly convert world coordinates to screen coordinates
                // This accounts for camera position, zoom, and viewport center offset
                const screenPoint = canvas.worldToScreen(target.x, target.y);
                targetX = screenPoint.x;
                targetY = screenPoint.y;
            } else {
                // Fallback if canvas not available yet (shouldn't happen, but safe)
                const camera = get(viewport);
                const viewportSize = { width: window.innerWidth, height: window.innerHeight };
                
                // Manual conversion: screenPos = (worldPos - camera) * zoom + viewportCenter
                targetX = (target.x - camera.x) * camera.zoom + viewportSize.width / 2;
                targetY = (target.y - camera.y) * camera.zoom + viewportSize.height / 2;
            }
            
            resetFadeTimeout();
        }
    });

    onMount(() => {
        currentX = window.innerWidth / 2;
        currentY = window.innerHeight / 2;
        targetX = currentX;
        targetY = currentY;

		updatePosition();
    });

    onDestroy(() => {
        if (animationFrame) {
            cancelAnimationFrame(animationFrame);
        }
        if (fadeTimeout) {
            clearTimeout(fadeTimeout);
        }
        unsubscribe();
    });
</script>

<div 
    class="cursor-container" 
    class:hidden={!isVisible}
    style="transform: translate({currentX}px, {currentY}px);"
>
    <svg 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        class="cursor-arrow"
    >
        <path 
            d="M5 3L19.5 12L11 13.5L7.5 21L5 3Z" 
            fill="#a855f7"
            stroke="#7c3aed"
            stroke-width="1.5"
            stroke-linejoin="round"
        />
    </svg>
    <div class="cursor-badge">
        Rool AI
    </div>
</div>

<style>
    .cursor-container {
        position: fixed;
        top: 0;
        left: 0;
        pointer-events: none;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        opacity: 1;
        transition: opacity 0.8s ease-out;
    }

    .cursor-container.hidden {
        opacity: 0;
    }

    .cursor-arrow {
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
    }

    .cursor-badge {
        background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
        color: white;
        padding: 4px 10px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(168, 85, 247, 0.4);
        margin-left: 20px;
    }
</style>

